/* eslint-disable @typescript-eslint/require-await */
import { inject, Injectable } from "@angular/core";
import { TenantPublicInfo } from "../models/tenant.model";
import { LoggerService } from "src/ancestors/logger.service";

export interface PWAManifest {
    name: string;
    short_name: string;
    description: string;  // Required, not optional
    start_url: string;
    scope: string;       // Required for PWA
    id: string;          // Required for identity
    display: string;
    background_color: string;
    theme_color: string;
    orientation?: string; // Optional, with valid values
    lang: string;        // Required for accessibility
    dir: "ltr" | "rtl" | "auto"; // Required for text direction
    icons: PWAIcon[];
    categories: string[];
}

export interface PWAIcon {
    src: string;
    sizes: string;
    type?: string;
    purpose?: string;
}

export interface IOSSplashScreen {
    href: string;
    media: string;
    sizes?: string;
}

export interface IOSMetaTag {
    name: string;
    content: string;
}

@Injectable({
    providedIn: "root"
})
export class ManifestService {
    
    private logger: LoggerService = inject(LoggerService);

    private tenant: TenantPublicInfo | null = null;

    public setTenant(tenant: TenantPublicInfo) {
        this.tenant = tenant;
    }

    /**
     * Genera il manifest dinamico basato sul tenant corrente
     * Segue le specifiche W3C Web App Manifest e best practices PWA
     * @returns Promise con il manifest conforme agli standard
     * @throws {Error} Se il tenant non è valido
     */
    async generateManifest(): Promise<PWAManifest> {
        if (!this.tenant?.title) {
            this.logger.error("Invalid tenant for manifest generation");
            throw new Error("Invalid tenant for manifest generation");
        }

        const primaryColor = this.tenant.primaryColor ?? "#000000";
        const baseUrl = window.location.origin;
        const appPath = "/";

        // Genera un ID univoco e stabile per l'app
        const appId = `${baseUrl}${appPath}`;

        const manifest: PWAManifest = {
            // Identificazione app (richiesti W3C)
            id: appId,
            name: this.tenant.title,
            short_name: this.truncateShortName(this.tenant.title),
            description: this.tenant.loginDescription || this.tenant.title,
            
            // URLs e scope (critici per PWA)
            start_url: appPath,
            scope: appPath,
            
            // Presentazione (richiesti per installabilità)
            display: "standalone",
            background_color: primaryColor,
            theme_color: primaryColor,
            
            // Internazionalizzazione (richiesti W3C)
            lang: "it",
            dir: "ltr",
            
            // Orientamento (valore valido)
            orientation: "any",
            
            // Risorse
            icons: this.generateStandardCompliantIcons(baseUrl),
            categories: ["business", "productivity", "utilities"]
        };
        
        return manifest;
    }

    /**
     * Aggiorna il manifest nel DOM seguendo best practices di sicurezza
     * Evita blob URLs per migliorare sicurezza e performance
     * @param manifest - Manifest da applicare
     */
    updateManifest(manifest: PWAManifest): void {
        // Valida manifest prima di applicarlo
        this.validateManifest(manifest);

        // Rimuovi manifest esistente
        const existingLink = document.querySelector("link[rel=\"manifest\"]");
        if (existingLink) {
            existingLink.remove();
        }

        // Invece di blob URL, crea manifest come data URI (più sicuro)
        const manifestJson = JSON.stringify(manifest, null, 2);
        const manifestDataUri = `data:application/json;charset=utf-8,${encodeURIComponent(manifestJson)}`;

        // Aggiungi nuovo link manifest
        const link = document.createElement("link");
        link.rel = "manifest";
        link.href = manifestDataUri;
        link.crossOrigin = "anonymous"; // Sicurezza aggiuntiva
        document.head.appendChild(link);

        // Aggiorna theme-color
        this.updateThemeColor(manifest.theme_color);

        // Aggiorna viewport meta per PWA
        this.updateViewportMeta();

        // Applica supporto iOS specifico
        this.updateIOSSupport(manifest);
    }


    /**
     * Aggiorna il theme-color nel DOM
     * @param color - Colore del tema
     */
    private updateThemeColor(color: string): void {
        let themeColorMeta = document.querySelector("meta[name=\"theme-color\"]");

        if (!themeColorMeta) {
            themeColorMeta = document.createElement("meta");
            themeColorMeta.setAttribute("name", "theme-color");
            document.head.appendChild(themeColorMeta);
        }

        themeColorMeta.setAttribute("content", color);
    }

    /**
     * Applica supporto iOS specifico per PWA
     * @param manifest - Manifest con le configurazioni
     */
    private updateIOSSupport(manifest: PWAManifest): void {
        // Pulisci elementi iOS esistenti
        this.cleanupIOSElements();

        // Aggiungi meta tags iOS
        this.addIOSMetaTags(manifest);

        // Aggiungi icone Apple Touch
        this.addAppleTouchIcons(window.location.origin);

        // Aggiungi splash screens iOS
        // this.addIOSSplashScreens(window.location.origin);
    }

    /**
     * Rimuove elementi iOS esistenti dal DOM
     */
    private cleanupIOSElements(): void {
        // Rimuovi meta tags iOS esistenti
        const iosMetaSelectors = [
            "meta[name=\"apple-mobile-web-app-capable\"]",
            "meta[name=\"apple-mobile-web-app-status-bar-style\"]",
            "meta[name=\"apple-mobile-web-app-title\"]",
            "meta[name=\"format-detection\"]"
        ];

        iosMetaSelectors.forEach(selector => {
            const existingMeta = document.querySelector(selector);
            if (existingMeta) {
                existingMeta.remove();
            }
        });

        // Rimuovi apple-touch-icon esistenti
        const existingAppleIcons = document.querySelectorAll("link[rel*=\"apple-touch-icon\"]");
        existingAppleIcons.forEach(icon => icon.remove());

        // Rimuovi splash screens esistenti
        const existingSplashScreens = document.querySelectorAll("link[rel=\"apple-touch-startup-image\"]");
        existingSplashScreens.forEach(screen => screen.remove());
    }

    /**
     * Aggiunge meta tags specifici per iOS
     * @param manifest - Manifest con le configurazioni
     */
    private addIOSMetaTags(manifest: PWAManifest): void {
        const iosMetaTags: IOSMetaTag[] = [
            {
                name: "apple-mobile-web-app-capable",
                content: "yes"
            },
            {
                name: "apple-mobile-web-app-status-bar-style",
                content: "default"
            },
            {
                name: "apple-mobile-web-app-title",
                content: manifest.short_name
            },
            {
                name: "format-detection",
                content: "telephone=no"
            }
        ];

        iosMetaTags.forEach(metaTag => {
            const meta = document.createElement("meta");
            meta.setAttribute("name", metaTag.name);
            meta.setAttribute("content", metaTag.content);
            document.head.appendChild(meta);
        });
    }

    /**
     * Aggiunge icone Apple Touch per iOS
     * @param baseUrl - URL base per le icone
     */
    private addAppleTouchIcons(baseUrl: string): void {
        const appleTouchIcons = [
            {
                href: `${baseUrl}/assets/img/icons/apple-touch-icon.png`,
                sizes: "180x180"
            },
            {
                href: `${baseUrl}/assets/img/icons/icon-152x152.png`,
                sizes: "152x152"
            },
            {
                href: `${baseUrl}/assets/img/icons/icon-144x144.png`,
                sizes: "144x144"
            },
            {
                href: `${baseUrl}/assets/img/icons/icon-120x120.png`,
                sizes: "120x120"
            },
            {
                href: `${baseUrl}/assets/img/icons/icon-114x114.png`,
                sizes: "114x114"
            },
            {
                href: `${baseUrl}/assets/img/icons/icon-76x76.png`,
                sizes: "76x76"
            },
            {
                href: `${baseUrl}/assets/img/icons/icon-72x72.png`,
                sizes: "72x72"
            },
            {
                href: `${baseUrl}/assets/img/icons/icon-60x60.png`,
                sizes: "60x60"
            },
            {
                href: `${baseUrl}/assets/img/icons/icon-57x57.png`,
                sizes: "57x57"
            }
        ];

        appleTouchIcons.forEach(icon => {
            const link = document.createElement("link");
            link.rel = "apple-touch-icon";
            link.sizes = icon.sizes;
            link.href = icon.href;
            document.head.appendChild(link);
        });
    }

    /**
     * Aggiunge splash screens per dispositivi iOS
     * Solo le dimensioni essenziali per la maggior parte dei dispositivi attuali
     * @param baseUrl - URL base per le immagini
     */
    private addIOSSplashScreens(baseUrl: string): void {
        const splashScreens: IOSSplashScreen[] = [
            // iPhone SE, 6, 7, 8 (375x667) - Ancora molto comuni
            {
                href: `${baseUrl}/assets/img/splash/apple-launch-750x1334.png`,
                media: "(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
            },
            {
                href: `${baseUrl}/assets/img/splash/apple-launch-1334x750.png`,
                media: "(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
            },
            // iPhone X, XS, 11 Pro (375x812)
            {
                href: `${baseUrl}/assets/img/splash/apple-launch-1125x2436.png`,
                media: "(device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
            },
            {
                href: `${baseUrl}/assets/img/splash/apple-launch-2436x1125.png`,
                media: "(device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
            },
            // iPhone 12, 13, 14 (390x844) - Dimensioni moderne standard
            {
                href: `${baseUrl}/assets/img/splash/apple-launch-1170x2532.png`,
                media: "(device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
            },
            {
                href: `${baseUrl}/assets/img/splash/apple-launch-2532x1170.png`,
                media: "(device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
            },
            // iPhone Pro Max (428x926) - Dispositivi grandi attuali
            {
                href: `${baseUrl}/assets/img/splash/apple-launch-1284x2778.png`,
                media: "(device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
            },
            {
                href: `${baseUrl}/assets/img/splash/apple-launch-2778x1284.png`,
                media: "(device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
            },
            // iPad (768x1024) - Copre la maggior parte degli iPad
            {
                href: `${baseUrl}/assets/img/splash/apple-launch-1536x2048.png`,
                media: "(device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
            },
            {
                href: `${baseUrl}/assets/img/splash/apple-launch-2048x1536.png`,
                media: "(device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
            }
        ];

        splashScreens.forEach(splashScreen => {
            const link = document.createElement("link");
            link.rel = "apple-touch-startup-image";
            link.href = splashScreen.href;
            link.media = splashScreen.media;
            document.head.appendChild(link);
        });

        // Fallback per splash screen generico
        const fallbackSplash = document.createElement("link");
        fallbackSplash.rel = "apple-touch-startup-image";
        fallbackSplash.href = `${baseUrl}/assets/img/splash/apple-launch-default.png`;
        document.head.appendChild(fallbackSplash);
    }

    /**
     * Genera lista completa di icone per diversi dispositivi iOS
     * @param url - URL base dell'applicazione
     * @returns Array di icone PWA con supporto iOS completo
     */
    private generateIOSCompliantIcons(url: string): PWAIcon[] {
        return [
            // Icone PWA standard
            {
                src: `${url}/assets/img/icons/icon-192x192.png`,
                sizes: "192x192",
                type: "image/png",
                purpose: "any"
            },
            {
                src: `${url}/assets/img/icons/icon-512x512.png`, 
                sizes: "512x512",
                type: "image/png",
                purpose: "any"
            },
            {
                src: `${url}/assets/img/icons/maskable-icon-192x192.png`,
                sizes: "192x192", 
                type: "image/png",
                purpose: "maskable"
            },
            {
                src: `${url}/assets/img/icons/maskable-icon-512x512.png`,
                sizes: "512x512",
                type: "image/png", 
                purpose: "maskable"
            },
            // Icone iOS specifiche
            {
                src: `${url}/assets/img/icons/apple-touch-icon.png`,
                sizes: "180x180",
                type: "image/png",
                purpose: "any"
            },
            {
                src: `${url}/assets/img/icons/icon-152x152.png`,
                sizes: "152x152",
                type: "image/png",
                purpose: "any"
            },
            {
                src: `${url}/assets/img/icons/icon-144x144.png`,
                sizes: "144x144",
                type: "image/png",
                purpose: "any"
            },
            {
                src: `${url}/assets/img/icons/icon-120x120.png`,
                sizes: "120x120",
                type: "image/png",
                purpose: "any"
            },
            {
                src: `${url}/assets/img/icons/icon-114x114.png`,
                sizes: "114x114",
                type: "image/png",
                purpose: "any"
            },
            {
                src: `${url}/assets/img/icons/icon-96x96.png`, 
                sizes: "96x96",
                type: "image/png",
                purpose: "any"
            },
            {
                src: `${url}/assets/img/icons/icon-76x76.png`,
                sizes: "76x76",
                type: "image/png",
                purpose: "any"
            },
            {
                src: `${url}/assets/img/icons/icon-72x72.png`,
                sizes: "72x72", 
                type: "image/png",
                purpose: "any"
            },
            {
                src: `${url}/assets/img/icons/icon-60x60.png`,
                sizes: "60x60",
                type: "image/png",
                purpose: "any"
            },
            {
                src: `${url}/assets/img/icons/icon-57x57.png`,
                sizes: "57x57",
                type: "image/png",
                purpose: "any"
            },
            {
                src: `${url}/assets/img/icons/icon-48x48.png`,
                sizes: "48x48",
                type: "image/png", 
                purpose: "any"
            }
        ];
    }

    /**
     * Aggiorna il viewport meta per ottimizzare PWA su mobile
     */
    private updateViewportMeta(): void {
        let viewportMeta = document.querySelector("meta[name=\"viewport\"]");
        
        if (!viewportMeta) {
            viewportMeta = document.createElement("meta");
            viewportMeta.setAttribute("name", "viewport");
            document.head.appendChild(viewportMeta);
        }
        
        // Viewport ottimale per PWA
        viewportMeta.setAttribute("content", "width=device-width, initial-scale=1.0, user-scalable=no, viewport-fit=cover");
    }

    /**
     * Tronca il nome per short_name secondo le best practices (max 12 caratteri)
     * @param fullName - Nome completo dell'app
     * @returns Nome abbreviato
     */
    private truncateShortName(fullName: string): string {
        if (fullName.length <= 12) {
            return fullName;
        }
        
        // Cerca di troncare alla prima parola se possibile
        const firstWord = fullName.split(" ")[0];
        if (firstWord.length <= 12) {
            return firstWord;
        }
        
        // Altrimenti tronca e aggiungi ellipsis
        return fullName.substring(0, 9) + "...";
    }

    /**
     * Genera lista di icone conformi agli standard PWA e W3C
     * Include dimensioni minime richieste e supporto cross-platform
     * @param url - URL base dell'applicazione
     * @returns Array di icone conformi agli standard
     */
    private generateStandardCompliantIcons(url: string): PWAIcon[] {
        return [
            // Icone PWA MINIME RICHIESTE per installabilità
            {
                src: `${url}/assets/img/icons/icon-192x192.png`,
                sizes: "192x192",
                type: "image/png",
                purpose: "any"
            },
            {
                src: `${url}/assets/img/icons/icon-512x512.png`, 
                sizes: "512x512",
                type: "image/png",
                purpose: "any"
            },
            // Icone maskable per Android adaptive icons (RACCOMANDATO)
            {
                src: `${url}/assets/img/icons/maskable-icon-192x192.png`,
                sizes: "192x192", 
                type: "image/png",
                purpose: "maskable"
            },
            {
                src: `${url}/assets/img/icons/maskable-icon-512x512.png`,
                sizes: "512x512",
                type: "image/png", 
                purpose: "maskable"
            },
            // Icone aggiuntive per migliore copertura dispositivi
            {
                src: `${url}/assets/img/icons/icon-144x144.png`,
                sizes: "144x144",
                type: "image/png",
                purpose: "any"
            },
            {
                src: `${url}/assets/img/icons/icon-96x96.png`, 
                sizes: "96x96",
                type: "image/png",
                purpose: "any"
            },
            {
                src: `${url}/assets/img/icons/icon-72x72.png`,
                sizes: "72x72", 
                type: "image/png",
                purpose: "any"
            },
            {
                src: `${url}/assets/img/icons/icon-48x48.png`,
                sizes: "48x48",
                type: "image/png", 
                purpose: "any"
            },
            // Icone specifiche iOS (per compatibilità)
            {
                src: `${url}/assets/img/icons/apple-touch-icon.png`,
                sizes: "180x180",
                type: "image/png",
                purpose: "any"
            }
        ];
    }

    /**
     * Valida che il manifest generato sia conforme agli standard PWA
     * @param manifest - Manifest da validare
     * @returns true se valido, throws Error se non valido
     */
    validateManifest(manifest: PWAManifest): boolean {

        // Campi obbligatori W3C
        if (!manifest.name?.trim()) {
            this.logger.error("'name' is required");
            return false;
        }
        if (!manifest.short_name?.trim()) {
            this.logger.error("'short_name' is required");
            return false;
        }
        if (!manifest.start_url) {
            this.logger.error("'start_url' is required");
            return false;
        }
        if (!manifest.display) {
            this.logger.error("'display' is required");
            return false;
        }
        if (!manifest.icons || manifest.icons.length === 0) {
            this.logger.error("'icons' must contain at least one icon");
            return false;
        }

        // Verifica icone minime per PWA installabile
        const has192Icon = manifest.icons.some(icon => 
            icon.sizes.includes("192x192") && icon.purpose !== "maskable"
        );
        const has512Icon = manifest.icons.some(icon => 
            icon.sizes.includes("512x512") && icon.purpose !== "maskable"
        );

        if (!has192Icon) {
            this.logger.error("Request icon 192x192 for PWA installability");
            return false;
        }
        if (!has512Icon) {
            this.logger.error("Request icon 512x512 for PWA installability");
            return false;
        }

        // Verifica valori display validi
        const validDisplayModes = ["fullscreen", "standalone", "minimal-ui", "browser"];
        if (!validDisplayModes.includes(manifest.display)) {
            this.logger.error(`'display' must be one of: ${validDisplayModes.join(", ")}`);
            return false;
        }

        // Verifica short_name length (raccomandazione)
        if (manifest.short_name.length > 12) {
            this.logger.error("'short_name' should be ≤ 12 characters for better display");
            return false;
        }

        return true;
    }
}